<script lang="ts">
  import { onMount } from 'svelte';
  import type { BudgetSuggestion, SuggestionsMetadata } from '../../lib/budget/suggestions';
  import BudgetSuggestionCard from './BudgetSuggestionCard.svelte';
  import EmptyState from '../common/EmptyState.svelte';
  import Button from '../common/Button.svelte';
  import { getStorage } from '../../lib/storage';

  let {
    onAccept,
    onCustomize,
    onBulkComplete
  }: {
    onAccept: (suggestion: BudgetSuggestion, skipDataReload?: boolean) => Promise<void>;
    onCustomize: (suggestion: BudgetSuggestion) => void;
    onBulkComplete?: () => Promise<void>;
  } = $props();

  const storage = getStorage();

  // Start collapsed by default
  let isExpanded = $state(false);
  let isLoading = $state(true);
  let isFetching = $state(false); // Track if we're currently fetching
  let error = $state<string | null>(null);
  let suggestions = $state<BudgetSuggestion[]>([]);
  let metadata = $state<SuggestionsMetadata | null>(null);
  let dismissedSuggestions = $state<Set<string>>(new Set());
  let selectedSuggestions = $state<Set<string>>(new Set()); // Track selected suggestions
  let isProcessing = $state(false); // Track bulk operations

  onMount(() => {
    loadDismissedSuggestions();
    fetchSuggestions();
  });

  // Expose refresh function to parent
  export function refresh() {
    fetchSuggestions(true);
  }

  // Expose function to dismiss a category (used when budget is deleted)
  export async function dismissCategory(categoryName: string, days: number = 90) {
    try {
      const key = `budget_suggestion:${categoryName}`;
      await storage.dismissItem(key, days);

      // Add to local set
      dismissedSuggestions.add(categoryName);

      // Remove from current suggestions list if present
      suggestions = suggestions.filter(s => s.name !== categoryName);

      console.log(`Category "${categoryName}" dismissed for ${days} days`);
    } catch (err) {
      console.error('Error dismissing category:', err);
    }
  }

  function loadDismissedSuggestions() {
    try {
      // Get all dismissed items with prefix filter
      const dismissed = storage.getDismissedItems();
      const budgetSuggestionDismissals = dismissed
        .filter(key => key.startsWith('budget_suggestion:'))
        .map(key => key.replace('budget_suggestion:', ''));

      dismissedSuggestions = new Set(budgetSuggestionDismissals);

      // Clean up expired dismissals
      storage.clearExpiredActions();
    } catch (err) {
      console.error('Error loading dismissed suggestions:', err);
    }
  }

  async function fetchSuggestions(silent = false) {
    // Prevent concurrent fetches
    if (isFetching) {
      console.log('Already fetching suggestions, skipping...');
      return;
    }

    isFetching = true;

    // Only show loading state on initial load, not on refreshes
    if (!silent) {
      isLoading = true;
    }
    error = null;

    try {
      // Import necessary modules
      const { getTransactions, getBudgets } = await import('../../lib/storage.js');
      const { generateBudgetSuggestions } = await import('../../lib/budget/suggestions.js');

      // Load data from storage
      const [transactions, budgetsData] = await Promise.all([
        getTransactions(),
        getBudgets()
      ]);

      // Get monthly income if set
      const monthlyIncome = budgetsData.settings?.monthlyIncomeTarget;

      // Generate suggestions
      const result = generateBudgetSuggestions(
        transactions,
        budgetsData.budgets,
        {
          lookbackMonths: 6, // Look back 6 months for better data
          minTransactions: 5, // Require at least 5 transactions for confidence
          minConfidence: 0.6,
          monthlyIncome
        }
      );

      // Filter out dismissed suggestions immediately
      const filteredSuggestions = (result.suggestions || []).filter(
        s => !dismissedSuggestions.has(s.name)
      );

      suggestions = filteredSuggestions;
      metadata = result.metadata || null;

      console.log('Budget suggestions loaded:', {
        totalGenerated: result.suggestions?.length || 0,
        afterFiltering: filteredSuggestions.length,
        dismissed: dismissedSuggestions.size
      });
    } catch (err) {
      console.error('Error fetching suggestions:', err);
      error = err instanceof Error ? err.message : 'Failed to load suggestions';
    } finally {
      if (!silent) {
        isLoading = false;
      }
      isFetching = false;
    }
  }

  async function handleDismiss(suggestion: BudgetSuggestion) {
    try {
      // Add to dismissed set
      dismissedSuggestions = new Set([...dismissedSuggestions, suggestion.name]);

      // Save to storage with expiry time (30 days from now)
      const key = `budget_suggestion:${suggestion.name}`;
      await storage.dismissItem(key, 30);

      // Remove from suggestions list
      suggestions = suggestions.filter(s => s.name !== suggestion.name);
    } catch (err) {
      console.error('Error dismissing suggestion:', err);
    }
  }

  async function handleAccept(suggestion: BudgetSuggestion, skipDataReload = false) {
    try {
      await onAccept(suggestion, skipDataReload);

      // Only refresh if not skipping (used by bulk operations)
      if (!skipDataReload) {
        await fetchSuggestions(true);
      }
    } catch (error) {
      console.error('Failed to accept suggestion:', error);
      // Don't remove on error - let user try again
    }
  }

  function handleCustomize(suggestion: BudgetSuggestion) {
    onCustomize(suggestion);
    // Don't remove from suggestions - let the parent handle it after modal save
  }

  function toggleSelection(suggestionName: string) {
    const newSelection = new Set(selectedSuggestions);
    if (newSelection.has(suggestionName)) {
      newSelection.delete(suggestionName);
    } else {
      newSelection.add(suggestionName);
    }
    selectedSuggestions = newSelection;
  }

  function toggleSelectAll() {
    if (selectedSuggestions.size === suggestions.length) {
      // Deselect all
      selectedSuggestions = new Set();
    } else {
      // Select all
      selectedSuggestions = new Set(suggestions.map(s => s.name));
    }
  }

  async function handleAddSelected() {
    if (selectedSuggestions.size === 0) return;

    isProcessing = true;
    try {
      const selectedList = suggestions.filter(s => selectedSuggestions.has(s.name));

      // Add all selected budgets without individual parent data reloads
      for (const suggestion of selectedList) {
        await handleAccept(suggestion, true); // Skip individual reloads
      }

      // Clear selection
      selectedSuggestions = new Set();

      // Call parent to reload data once
      if (onBulkComplete) {
        await onBulkComplete();
      }

      // Then refresh our suggestions
      await fetchSuggestions(true);
    } catch (error) {
      console.error('Failed to add selected budgets:', error);
    } finally {
      isProcessing = false;
    }
  }

  async function handleDismissSelected() {
    if (selectedSuggestions.size === 0) return;

    isProcessing = true;
    try {
      // Add all selected to dismissed with expiry time (30 days)
      for (const name of selectedSuggestions) {
        const key = `budget_suggestion:${name}`;
        await storage.dismissItem(key, 30);
        dismissedSuggestions.add(name);
      }

      // Remove from suggestions list
      suggestions = suggestions.filter(s => !selectedSuggestions.has(s.name));

      // Clear selection
      selectedSuggestions = new Set();
    } catch (error) {
      console.error('Failed to dismiss selected:', error);
    } finally {
      isProcessing = false;
    }
  }

  function formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  }

  // Visible suggestions count
  const visibleSuggestionsCount = $derived(suggestions.length);
  const hasVisibleSuggestions = $derived(visibleSuggestionsCount > 0);
</script>

{#if isLoading}
  <div class="suggestions-section">
    <div class="loading">
      <p>Loading budget suggestions...</p>
    </div>
  </div>
{:else if error}
  <div class="suggestions-section">
    <div class="error">
      <p>Failed to load suggestions: {error}</p>
      <Button variant="secondary" size="sm" onclick={fetchSuggestions}>
        Retry
      </Button>
    </div>
  </div>
{:else if hasVisibleSuggestions}
  <div class="suggestions-section">
    <button
      class="suggestions-header"
      onclick={() => isExpanded = !isExpanded}
      type="button"
    >
      <div class="header-content">
        <h2 class="title">
          💡 Budget Suggestions
          <span class="count">({visibleSuggestionsCount})</span>
        </h2>
        {#if metadata && !isExpanded}
          <p class="summary">
            {formatCurrency(metadata.totalSuggested)} suggested
            {#if metadata.percentOfIncome > 0}
              <span class="income-percent">({metadata.percentOfIncome}% of income)</span>
            {/if}
          </p>
        {/if}
      </div>
      <span class="expand-icon" class:expanded={isExpanded}>
        ▼
      </span>
    </button>

    {#if isExpanded}
      <div class="bulk-actions">
        <label class="select-all-checkbox">
          <input
            type="checkbox"
            checked={selectedSuggestions.size > 0 && selectedSuggestions.size === suggestions.length}
            indeterminate={selectedSuggestions.size > 0 && selectedSuggestions.size < suggestions.length}
            onchange={toggleSelectAll}
            disabled={isProcessing}
          />
          <span>Select All ({selectedSuggestions.size} selected)</span>
        </label>
        <div class="action-buttons">
          <Button
            variant="primary"
            size="sm"
            onclick={handleAddSelected}
            disabled={selectedSuggestions.size === 0 || isProcessing}
          >
            Add Selected ({selectedSuggestions.size})
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onclick={handleDismissSelected}
            disabled={selectedSuggestions.size === 0 || isProcessing}
          >
            Dismiss Selected
          </Button>
        </div>
      </div>

      <div class="suggestions-list">
        {#each suggestions as suggestion (suggestion.name)}
          <BudgetSuggestionCard
            {suggestion}
            selected={selectedSuggestions.has(suggestion.name)}
            onToggleSelect={() => toggleSelection(suggestion.name)}
            onAccept={handleAccept}
            onCustomize={handleCustomize}
            onDismiss={handleDismiss}
            disabled={isProcessing}
          />
        {/each}
      </div>
    {/if}
  </div>
{/if}

<style lang="scss">
  .suggestions-section {
    margin-bottom: var(--spacing-2xl);
  }

  .suggestions-header {
    width: 100%;
    background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
    border: var(--border-width-thin) solid var(--color-primary-border);
    border-radius: var(--radius-lg);
    padding: var(--spacing-lg);
    cursor: pointer;
    transition: all var(--transition-base);
    display: flex;
    justify-content: space-between;
    align-items: center;
    text-align: left;

    &:hover {
      background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
      box-shadow: var(--shadow-md);
    }
  }

  .header-content {
    flex: 1;
  }

  .title {
    font-size: var(--font-size-xl);
    font-weight: var(--font-weight-bold);
    color: var(--color-primary-dark);
    margin: 0 0 var(--spacing-xs) 0;
    display: flex;
    align-items: center;
    gap: var(--spacing-sm);
  }

  .count {
    font-size: var(--font-size-base);
    font-weight: var(--font-weight-semibold);
    color: var(--color-primary);
  }

  .summary {
    font-size: var(--font-size-sm);
    color: var(--color-text-secondary);
    margin: 0;
  }

  .income-percent {
    color: var(--color-text-tertiary);
    margin-left: var(--spacing-xs);
  }

  .expand-icon {
    font-size: var(--font-size-sm);
    color: var(--color-primary);
    transition: transform var(--transition-base);
    margin-left: var(--spacing-md);

    &.expanded {
      transform: rotate(180deg);
    }
  }

  .bulk-actions {
    background: var(--color-background-secondary);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-md);
    padding: var(--spacing-sm) var(--spacing-md);
    margin-top: var(--spacing-md);
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: var(--spacing-md);
  }

  .select-all-checkbox {
    display: flex;
    align-items: center;
    gap: var(--spacing-sm);
    cursor: pointer;
    font-size: var(--font-size-sm);
    color: var(--color-text-secondary);

    input[type="checkbox"] {
      cursor: pointer;
      width: 16px;
      height: 16px;
    }

    &:hover {
      color: var(--color-text-primary);
    }
  }

  .action-buttons {
    display: flex;
    gap: var(--spacing-sm);
  }

  .suggestions-list {
    margin-top: var(--spacing-sm);
    background: var(--color-background);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-md);
    overflow: hidden;
  }

  .loading,
  .error {
    background: var(--color-background-secondary);
    border: var(--border-width-thin) solid var(--color-border);
    border-radius: var(--radius-lg);
    padding: var(--spacing-2xl);
    text-align: center;

    p {
      font-size: var(--font-size-base);
      color: var(--color-text-secondary);
      margin: 0 0 var(--spacing-md) 0;
    }
  }

  .error {
    border-color: var(--color-danger-border);
    background: var(--color-danger-light);

    p {
      color: var(--color-danger-dark);
    }
  }

  // Mobile responsiveness
  @media (max-width: 768px) {
    .bulk-actions {
      flex-direction: column;
      align-items: stretch;
    }

    .action-buttons {
      flex-direction: column;

      :global(.btn) {
        width: 100%;
      }
    }

    .suggestions-list {
      gap: var(--spacing-sm);
    }

    .suggestions-header {
      padding: var(--spacing-md);
    }

    .title {
      font-size: var(--font-size-lg);
    }

    .summary {
      font-size: var(--font-size-xs);
    }
  }
</style>
